import React from 'react';
import DocumentNumberField from '../components/DocumentNumberField';
import WeekNumberField from '../components/WeekNumberField';
import FormActions from './FormActions';
import { Input, Select, FieldSet } from '../../../components/ui';
import {
  PPM_TYPE_OPTIONS,
  SC_STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  ZONE_OPTIONS,
} from '../QDFConstants';

const PreventiveMaintenanceForm = ({
  formData,
  handleChange,
  handleSubmit,
  loading,
  generatingNumber,
  onCancel,
  isEditing = false,
  shopConfig = null,
}) => {
  const groupedAssetTags = shopConfig?.groupedAssetTags || [];
  const assetNumbers = shopConfig?.assetNumbers || [];
  const locations = shopConfig?.locations || [];
  const departments = shopConfig?.departments || [];

  const isMonthly = formData.ppmType === 'Monthly';
  const autoFilled = 'Filled automatically from the selected asset tag.';

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <FieldSet legend="Schedule">
        <div className="grid gap-4 md:grid-cols-2">
          <Select
            id="ppmType"
            name="ppmType"
            label="PPM type"
            required
            value={formData.ppmType}
            onChange={handleChange}
          >
            <option value="">Select PPM type</option>
            {PPM_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Select>

          <WeekNumberField formData={formData} />
        </div>
      </FieldSet>

      <FieldSet legend="Maintenance details">
        <div className="grid gap-4 md:grid-cols-2">
          <DocumentNumberField type="PM" formData={formData} generatingNumber={generatingNumber} />

          <Select
            id="scStatus"
            name="scStatus"
            label="PM status"
            value={formData.scStatus}
            onChange={handleChange}
          >
            <option value="">Select status</option>
            {SC_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Select>

          <Input
            id="creationDate"
            name="creationDate"
            type="datetime-local"
            label="Creation date & time"
            value={formData.creationDate}
            onChange={handleChange}
          />

          <Input
            id="contractor"
            name="contractor"
            label="Contractor"
            value={formData.contractor}
            onChange={handleChange}
          />

          <Select
            id="priority"
            name="priority"
            label="Priority"
            value={formData.priority}
            onChange={handleChange}
            className="md:col-span-2"
          >
            <option value="">Select priority</option>
            {PRIORITY_OPTIONS.map((priority) => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </Select>

          <Input
            id="targetResponse"
            name="targetResponse"
            type="datetime-local"
            label="Target response"
            value={formData.targetResponse}
            onChange={handleChange}
          />

          <Input
            id="targetResolution"
            name="targetResolution"
            type="datetime-local"
            label="Target resolution"
            value={formData.targetResolution}
            onChange={handleChange}
          />
        </div>
      </FieldSet>

      <FieldSet
        legend="Asset details"
        description={
          isMonthly
            ? 'Monthly PPM covers a zone rather than a single asset.'
            : undefined
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          {isMonthly ? (
            <>
              <Select
                id="location"
                name="location"
                label="Location"
                value={formData.location}
                onChange={handleChange}
              >
                <option value="">Select location</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </Select>

              <Select
                id="zone"
                name="zone"
                label="Zone"
                value={formData.zone}
                onChange={handleChange}
                disabled={!formData.location}
                hint={!formData.location ? 'Select a location first.' : undefined}
              >
                <option value="">Select zone</option>
                {formData.location &&
                  ZONE_OPTIONS[formData.location] &&
                  ZONE_OPTIONS[formData.location].map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
              </Select>
            </>
          ) : (
            <>
              <Select
                id="assetTag"
                name="assetTag"
                label="Asset tag"
                value={formData.assetTag}
                onChange={handleChange}
              >
                <option value="">Select asset tag</option>
                {groupedAssetTags.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((tag) => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </optgroup>
                ))}
              </Select>

              <Select
                id="assetNumber"
                name="assetNumber"
                label="Asset number"
                value={formData.assetNumber}
                onChange={handleChange}
                disabled
                hint={autoFilled}
              >
                <option value="">Select asset number</option>
                {assetNumbers.map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Select>

              <Select
                id="location"
                name="location"
                label="Location"
                value={formData.location}
                onChange={handleChange}
                disabled
                hint={autoFilled}
              >
                <option value="">Select location</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </Select>

              <Select
                id="department"
                name="department"
                label="Department"
                value={formData.department}
                onChange={handleChange}
                disabled
                hint={autoFilled}
              >
                <option value="">Select department</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </Select>
            </>
          )}
        </div>
      </FieldSet>

      <FormActions onCancel={onCancel} loading={loading} isEditing={isEditing} />
    </form>
  );
};

export default PreventiveMaintenanceForm;
